// @flow
import { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { animated, useSpring } from 'react-spring/web.cjs';
import { SPACING, COLORS } from '@tablekit/theme';
import BodyPadding from 'styled/BodyPadding';
import { MAX_WIDTH, MOBILE_BREAKPOINT } from 'utils/theme';
import useOnScroll from 'utils/useOnScroll';
import { CallToActionBackground } from 'components/CallToAction';
import renderComponent from 'components/editable/renderComponent';
import HeroBanner from 'components/HeroBanner';
import type {
  BlokComponentPropsType,
  BlokComponentType,
  SimpleContentType,
  TestimonialType,
  ParagraphWithImageType,
  CardsType
} from 'components/types';
import * as componentKeys from 'componentKeys';
import componentMap from './componentMap';

type BlokPropsType = {
  body: BlokComponentType<>[],
  call_to_action: BlokComponentType<SimpleContentType>[],
  disable_background?: boolean,
  hero_content: BlokComponentType<SimpleContentType>[],
  hide_from_search_engines: boolean,
  post_body: BlokComponentType<
    SimpleContentType | ParagraphWithImageType | TestimonialType | CardsType
  >[],
  split_background?: boolean
};

export const Column = styled.div`
  max-width: ${MAX_WIDTH};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;

  ${({ useBottomPadding }) =>
    useBottomPadding
      ? css`
          padding-bottom: ${SPACING.L9};
          @media ${MOBILE_BREAKPOINT} {
            padding-bottom: ${SPACING.L7};
          }
        `
      : ''};
`;

const ColumnBackground = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 100vw;
  background: white;
  z-index: -2;
`;

const ColumnSliceBackground = styled(ColumnBackground)`
  background: linear-gradient(180deg, transparent 49.9%, white 50%);
`;

const backgroundSvgHeight = 526;

const BackgroundIconPositioner = animated(styled.div`
  position: absolute;
  left: ${SPACING.L8};
  right: ${SPACING.L8};
  margin: 0 auto;
  max-width: ${MAX_WIDTH};
  min-width: ${2 * backgroundSvgHeight}px;
  top: 100%;
  height: ${backgroundSvgHeight}px;
  z-index: -2;

  @media (max-width: ${2 * backgroundSvgHeight}px) {
    left: 50%;
    transform: translateX(-50%);
  }

  @media ${MOBILE_BREAKPOINT} {
    display: none;
  }
`);

const ColumnGradient = styled(ColumnBackground)`
  overflow: hidden;
  background: linear-gradient(141deg, white 0%, ${COLORS.GRAY.L2} 97%);
`;

const SplitContainer = styled.div`
  position: relative;
`;

const LeftIcon = styled.svg`
  position: absolute;
  left: -${backgroundSvgHeight * 0.4}px;
  transform: rotate(15deg);
`;

const RightIcon = styled.svg`
  position: absolute;
  right: -${backgroundSvgHeight * 0.4}px;
  transform: rotate(165deg);
`;

const backgroundIconPath = (
  <path
    strokeWidth="1"
    stroke={COLORS.PURPLE.L1}
    fill="none"
    d="M330.69395,4.54747351e-13 C257.829181,28.024911 190.569395,72.8647687 130.782918,130.782918 C72.8647687,188.701068 28.024911,257.829181 4.54747351e-13,330.69395 L194.30605,525 C198.042705,440.925267 233.540925,360.587189 297.064057,297.064057 C360.587189,233.540925 442.793594,199.911032 525,194.30605 L330.69395,4.54747351e-13"
  />
);

const CONTENT_WITH_IMAGE_COMPONENTS = [
  componentKeys.PARAGRAPH_WITH_IMAGE,
  componentKeys.TEAM_PROFILE
];

const Page = ({
  id,
  blok: {
    body = [],
    hero_content: heroContent = [],
    post_body: postBodyContent = [],
    disable_background: isBackgroundDisabled,
    split_background: shouldSplitBackground
  }
}: BlokComponentPropsType<BlokPropsType>) => {
  const [currentOpenBlok, setOpenBlok] = useState('');
  let contentWithImageIsRight = true;
  let angledBackgroundGroupIsRight = false;
  const renderBodyBlok = (isPostBody) => (bodyBlok) => {
    if (!bodyBlok) return null;
    const staticProps = {
      isRight: false,
      isPostBody,
      isBackgroundDisabled,
      customPath: 'components/editable/Page/index.jsx',
      isOpen: bodyBlok._uid === currentOpenBlok,
      toggleBlok: () => {
        if (bodyBlok._uid === currentOpenBlok) {
          setOpenBlok('');
        } else {
          setOpenBlok(bodyBlok._uid);
        }
      }
    };
    if (bodyBlok.component === componentKeys.ANGLED_BACKGROUND_GROUP) {
      staticProps.isRight = angledBackgroundGroupIsRight;
      angledBackgroundGroupIsRight = !angledBackgroundGroupIsRight;
      return renderComponent(bodyBlok, staticProps, componentMap);
    }
    if (CONTENT_WITH_IMAGE_COMPONENTS.indexOf(bodyBlok.component) !== -1) {
      staticProps.isRight = contentWithImageIsRight;
      contentWithImageIsRight = !contentWithImageIsRight;
    }
    return (
      <BodyPadding key={bodyBlok._uid}>
        {renderComponent(bodyBlok, staticProps, componentMap)}
      </BodyPadding>
    );
  };
  if (isBackgroundDisabled) {
    const contentBlocks = body.concat(postBodyContent);
    return (
      <div id={id}>
        <HeroBanner blok={heroContent[0]} isCurved />
        <Column>
          {contentBlocks.map(renderBodyBlok())}
          <CallToActionBackground />
        </Column>
      </div>
    );
  }
  let borderBlock;
  let adjustedPostBodyContent = postBodyContent;
  let adjustedBodyContent = body;
  if (shouldSplitBackground) {
    if (postBodyContent.length) {
      [borderBlock, ...adjustedPostBodyContent] = postBodyContent;
    } else if (body.length >= 2) {
      borderBlock = body[body.length - 1];
      adjustedBodyContent = body.slice(0, -1);
    }
  }
  const parallaxRef = useRef();
  const parallaxContainerRef = useRef();
  const [styles, setSpring] = useSpring(() => ({
    top: 0,
    tension: 500
  }));
  const updateOnScroll = () => {
    const windowHeight = window.innerHeight;
    let movement = 0;
    // How much faster or slower the animation should happen relative to scrolling
    const animationScrollSpeed = 1.4;
    if (shouldSplitBackground) {
      if (!parallaxRef.current) return;
      const { top, height } = parallaxRef.current.getBoundingClientRect();
      const splitCenterAboveBottomOfWindow =
        Math.max(0, -(top - windowHeight + height / 2)) +
        backgroundSvgHeight * 0.2;
      movement =
        height / 2 +
        Math.min(
          splitCenterAboveBottomOfWindow * animationScrollSpeed,
          backgroundSvgHeight * 0.9
        );
    } else {
      if (!parallaxContainerRef.current) return;
      const { bottom } = parallaxContainerRef.current.getBoundingClientRect();
      const columnAboveBottomOfWindow = Math.min(
        0,
        // value is the percentage from the top of the page when the animation starts
        bottom - windowHeight * 0.9
      );
      movement = Math.min(
        -columnAboveBottomOfWindow * animationScrollSpeed,
        backgroundSvgHeight * 0.9
      );
    }
    setSpring({
      top: movement
    });
  };
  useOnScroll(updateOnScroll);
  return (
    <div id={id}>
      <HeroBanner blok={heroContent[0]} isCurved />
      <Column
        ref={parallaxContainerRef}
        useBottomPadding={!shouldSplitBackground}
      >
        {adjustedBodyContent.map(renderBodyBlok())}
        <ColumnGradient>
          <BackgroundIconPositioner
            style={{
              top: styles.top.interpolate((n) => `calc(100% - ${n}px)`)
            }}
          >
            <LeftIcon
              width="527px"
              height={`${backgroundSvgHeight}px`}
              viewBox="0 0 527 526"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              {backgroundIconPath}
            </LeftIcon>
            <RightIcon
              width="527px"
              height={`${backgroundSvgHeight}px`}
              viewBox="0 0 527 526"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              {backgroundIconPath}
            </RightIcon>
          </BackgroundIconPositioner>
        </ColumnGradient>
        {borderBlock && (
          <SplitContainer ref={parallaxRef}>
            {renderBodyBlok(true)(borderBlock)}
            <ColumnSliceBackground />
          </SplitContainer>
        )}
      </Column>
      <Column useBottomPadding>
        {!!adjustedPostBodyContent.length &&
          adjustedPostBodyContent.map(renderBodyBlok(true))}
        <CallToActionBackground isWhite />
      </Column>
    </div>
  );
};

export default Page;
